import { PriceSlider } from "@/components/PriceSlider";
import { formatAmountRange } from "@/utils/formatAmountRange";
import { getCurrencyCode } from "@/utils/getCurrency";
import { type WidgetSettingsDefinition } from "@repo/api";
import { usePriceRange } from "@repo/api";
import { Button, ListViewItem, Loading, moneyCashIcon } from "@repo/skapa";
import { getLanguageLocal, t } from "@repo/translation";
import {
  ModalSheet,
  SectionContainer,
  translateKey,
  useSettings,
} from "@repo/ui";
import { useState } from "react";

export const ValueRangeSettingsControl = ({
  settingDefinition,
  setting,
  setSetting,
}: {
  settingDefinition: WidgetSettingsDefinition;
  setting: Record<string, any>;
  setSetting: (newSetting: Record<string, object>) => void;
}) => {
  const value = setting[settingDefinition.id];

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [newValue, setNewValue] = useState(value);

  const { data: priceRange, isLoading } = usePriceRange();

  const { general, market, language } = useSettings();
  const locale = getLanguageLocal(language, market);
  const currency = getCurrencyCode(general);

  if (isLoading) {
    return <Loading size="medium" />;
  }

  // *2 here is to have a good start max value based on current values
  // the coworker can type in any value in the input field
  const calculatedMax =
    (priceRange?.maxAmount ? priceRange?.maxAmount * 2 : undefined) ?? 999999;

  return (
    <>
      <ListViewItem
        control="navigational"
        title={translateKey(settingDefinition.title)}
        description={formatAmountRange(
          value,
          currency,
          locale,
          Boolean(general?.localization?.disable_decimals)
        )}
        onClick={() => setModalIsOpen(true)}
        leadingIcon={moneyCashIcon}
      />
      <ModalSheet
        title={translateKey(settingDefinition.title)}
        visible={modalIsOpen}
        onClose={() => setModalIsOpen(false)}
        onBack={() => setModalIsOpen(false)}
        footer={
          <>
            <Button
              type="primary"
              text={t("action.confirm")}
              onClick={() => {
                setSetting({
                  ...setting,
                  [settingDefinition.id]: newValue,
                });
                setModalIsOpen(false);
              }}
            />
            <Button
              type="secondary"
              text={t("action.delete")}
              onClick={() => {
                setSetting({
                  ...setting,
                  [settingDefinition.id]: undefined,
                });
                setModalIsOpen(false);
              }}
            />
          </>
        }
      >
        <SectionContainer>
          <PriceSlider
            id="value"
            currency={getCurrencyCode(general)}
            minAmount={0}
            maxAmount={calculatedMax}
            value={newValue ?? [0, calculatedMax]}
            onChange={setNewValue}
          />
        </SectionContainer>
      </ModalSheet>
    </>
  );
};
